<script setup lang="ts">
import { computed } from 'vue';

import MinusIcon from '@/components/base/assets/MinusIcon.vue';
import PlusIcon from '@/components/base/assets/PlusIcon.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';

interface Props {
  ariaLabel?: string;
  max?: number;
  min?: number;
  modelValue: number;
  removeOnMinimum?: boolean;
  size?: 'default' | 'small';
}

const props = withDefaults(defineProps<Props>(), {
  ariaLabel: 'Quantity',
  min: 1,
  size: 'default',
});

const emit = defineEmits(['update:modelValue', 'remove']);

const quantity = computed({
  get: () => props.modelValue,
  set: (val) => {
    const newValue =
      props.modelValue > val ? Math.max(props.min, val) : Math.min(props.max ?? Infinity, val);
    if (newValue === props.min && props.removeOnMinimum) emit('remove');
    if (newValue !== props.modelValue) emit('update:modelValue', newValue);
  },
});

const minimumAllowedValue = computed(() => (props.removeOnMinimum ? props.min + 1 : props.min));

const padding = computed(() => {
  if (props.size === 'small') return 'p-1';
  return 'p-2';
});
</script>

<template>
  <div
    class="inline-flex items-center overflow-hidden bg-white border border-solid rounded-full gap-x-1 border-neutral-300"
    data-test="quantity-stepper"
  >
    <UnstyledButton
      aria-hidden="true"
      class="flex items-center justify-center h-full hover:bg-neutral-100 active:bg-neutral-200"
      :class="padding"
      data-test="quantity-stepper-remove-button"
      :disabled="quantity <= props.min"
      tabindex="-1"
      @click="quantity -= 1"
    >
      <MinusIcon class="md:my-px" :size="16" />
    </UnstyledButton>
    <SmallBodyText
      :aria-label="ariaLabel"
      :aria-valuemax="max"
      :aria-valuemin="min"
      :aria-valuenow="quantity"
      class="min-w-[0.875rem] text-center mt-0"
      role="spinbutton"
      tabindex="0"
      @keydown.down.prevent="quantity -= 1"
      @keydown.home.prevent="quantity = minimumAllowedValue"
      @keydown.page-down.prevent="quantity -= 5"
      @keydown.page-up.prevent="quantity += 5"
      @keydown.up.prevent="quantity += 1"
    >
      <slot :quantity="quantity">{{ quantity }}</slot>
    </SmallBodyText>
    <UnstyledButton
      aria-hidden="true"
      class="flex items-center justify-center h-full hover:bg-neutral-100 active:bg-neutral-200"
      :class="padding"
      data-test="quantity-stepper-add-button"
      :disabled="max ? quantity >= max : undefined"
      tabindex="-1"
      @click="quantity += 1"
    >
      <PlusIcon class="md:my-px" :size="16" />
    </UnstyledButton>
  </div>
</template>
